import { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, Send, Users, Check } from 'lucide-react';
import type { CityOverlapGroup } from '../../utils/overlapGroups';
import type { MeetupProposalMetadata } from '../../types';
import { getCityName } from '../../utils/airports';
import { formatDateShort, toDateStr } from '../../utils/dates';
import { useApp } from '../../context/AppContext';
import { AvatarImg } from '../shared/Avatar';
import { getAirline } from '../../utils/airlines';
import { haptic, hapticMedium } from '../../utils/haptics';
import { generateGroupChatName } from '../../utils/groupChatName';
import { Capacitor } from '@capacitor/core';
import { showTimePicker } from '../../services/timePicker';
import { CityOverlapCard } from '../overlaps/CityOverlapCard';
import { maybeRequestReview } from '../../services/appReview';

interface NudgeSheetProps {
  group: CityOverlapGroup;
  open: boolean;
  onClose: () => void;
  /** Pre-select this date when opening (e.g. the date the user tapped) */
  initialDate?: string;
}

const QUICK_MESSAGES = [
  'Drinks?',
  'Dinner?',
  'Coffee?',
  'Explore the city?',
];

/** Generate hourly time slots — full 24h range, optionally narrowed by overlap window */
function buildTimeSlots(startTime?: string, endTime?: string): string[] {
  const startH = startTime ? parseInt(startTime.split(':')[0], 10) : 0;
  const endH = endTime ? parseInt(endTime.split(':')[0], 10) : 23;
  const from = startTime && parseInt(startTime.split(':')[1], 10) > 0 ? startH + 1 : startH;
  const slots: string[] = [];
  for (let h = from; h <= endH; h++) {
    slots.push(`${String(h).padStart(2, '0')}:00`);
  }
  return slots.length > 0 ? slots : buildTimeSlots(); // fallback to full range
}

function formatTime12(t: string): string {
  const [h, m] = t.split(':').map(Number);
  const mm = String(m ?? 0).padStart(2, '0');
  const suffix = h < 12 ? 'AM' : 'PM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${mm} ${suffix}`;
}


export function NudgeSheet({ group, open, onClose, initialDate }: NudgeSheetProps) {
  const { sendMessage, createGroupChat, sendGroupMessage, createMeetup, currentUser } = useApp();
  const navigate = useNavigate();
  const [selectedFriends, setSelectedFriends] = useState<Set<string>>(() => {
    return new Set(group.friendStays.map((fs) => fs.user.id));
  });
  const [message, setMessage] = useState('');
  const [quickPick, setQuickPick] = useState<string | null>(null);
  const [meetDate, setMeetDate] = useState(initialDate ?? '');
  const [meetHour, setMeetHour] = useState(19);
  const [meetMinute, setMeetMinute] = useState(0);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dateScrollRef = useRef<HTMLDivElement>(null);
  const hasScrolledOnce = useRef(false);

  // Deduplicate friends
  const friends = useMemo(() => {
    const friendMap = new Map<string, typeof group.friendStays[0]>();
    for (const fs of group.friendStays) {
      if (!friendMap.has(fs.user.id)) friendMap.set(fs.user.id, fs);
    }
    return [...friendMap.values()];
  }, [group.friendStays]);

  // Build a filtered group with only selected friends for the Gantt
  const filteredGroup = useMemo(() => ({
    ...group,
    friendStays: group.friendStays.filter((fs) => selectedFriends.has(fs.user.id)),
  }), [group, selectedFriends]);

  // Compute date window for selected friends using ALL friendStay entries
  // (a friend may have multiple entries for disjoint overlap fragments at home base).
  // Uses union of all overlapWithMe ranges to build the full date window.
  const dateWindow = useMemo(() => {
    // Use ALL friendStays (not deduped) so fragmented home-base overlaps are included
    const allSelectedStays = group.friendStays.filter((fs) => selectedFriends.has(fs.user.id));
    if (allSelectedStays.length === 0) return null;

    // Build friendsByDate map (date -> set of friend IDs present)
    const friendsByDate = new Map<string, Set<string>>();
    for (const fs of allSelectedStays) {
      const d = new Date(fs.overlapWithMe.startDate + 'T12:00:00');
      const endD = new Date(fs.overlapWithMe.endDate + 'T12:00:00');
      while (d <= endD) {
        const ds = toDateStr(d);
        if (!friendsByDate.has(ds)) friendsByDate.set(ds, new Set());
        friendsByDate.get(ds)!.add(fs.user.id);
        d.setDate(d.getDate() + 1);
      }
    }

    // Collect all overlapWithMe ranges, sorted by start date
    const ranges = allSelectedStays
      .map((fs) => ({
        start: fs.overlapWithMe.startDate,
        end: fs.overlapWithMe.endDate,
        startTime: fs.overlapWithMe.startTime,
        endTime: fs.overlapWithMe.endTime,
      }))
      .sort((a, b) => a.start.localeCompare(b.start));

    // Merge overlapping/adjacent ranges
    const merged: typeof ranges = [];
    for (const r of ranges) {
      const last = merged[merged.length - 1];
      if (last && r.start <= last.end) {
        if (r.end > last.end) {
          last.end = r.end;
          last.endTime = r.endTime;
        }
      } else {
        merged.push({ ...r });
      }
    }

    // Determine mode: if all selected friends are on every date → intersection, else union
    const uniqueFriendIds = new Set(allSelectedStays.map((fs) => fs.user.id));
    const allDates = [...friendsByDate.keys()].sort();
    const allPresent = allDates.length > 0 && allDates.every((d) => (friendsByDate.get(d)?.size ?? 0) >= uniqueFriendIds.size);

    return {
      mode: (allPresent ? 'intersection' : 'union') as 'intersection' | 'union',
      start: merged[0].start,
      end: merged[merged.length - 1].end,
      startTime: merged[0].startTime,
      endTime: merged[merged.length - 1].endTime,
      ranges: merged,
      friendsByDate,
    };
  }, [group.friendStays, selectedFriends]);

  // Intersection dates: only dates where ALL selected friends overlap
  const intersectionDates = useMemo(() => {
    if (!dateWindow) return [];
    return [...dateWindow.friendsByDate.entries()]
      .filter(([, ids]) => ids.size >= selectedFriends.size)
      .map(([d]) => d)
      .sort();
  }, [dateWindow, selectedFriends]);

  // Default meetDate: prefer tapped date if it's in the intersection, else first intersection date
  const effectiveMeetDate = useMemo(() => {
    if (intersectionDates.length === 0) return '';
    if (meetDate && intersectionDates.includes(meetDate)) return meetDate;
    return intersectionDates[0];
  }, [meetDate, intersectionDates]);

  // Narrow time slots based on which friends are present on the selected date
  const timeSlots = useMemo(() => {
    if (!dateWindow || !effectiveMeetDate) return buildTimeSlots();

    // Find all selected friends' stays that cover this date
    const allSelectedStays = group.friendStays.filter((fs) =>
      selectedFriends.has(fs.user.id) &&
      effectiveMeetDate >= fs.overlapWithMe.startDate &&
      effectiveMeetDate <= fs.overlapWithMe.endDate
    );
    if (allSelectedStays.length === 0) return buildTimeSlots();

    // Intersect availability windows: latest arrival, earliest departure
    let latestStart: string | undefined;
    let earliestEnd: string | undefined;
    for (const fs of allSelectedStays) {
      if (effectiveMeetDate === fs.overlapWithMe.startDate && fs.overlapWithMe.startTime) {
        if (!latestStart || fs.overlapWithMe.startTime > latestStart) {
          latestStart = fs.overlapWithMe.startTime;
        }
      }
      if (effectiveMeetDate === fs.overlapWithMe.endDate && fs.overlapWithMe.endTime) {
        if (!earliestEnd || fs.overlapWithMe.endTime < earliestEnd) {
          earliestEnd = fs.overlapWithMe.endTime;
        }
      }
    }
    return buildTimeSlots(latestStart, earliestEnd);
  }, [group.friendStays, selectedFriends, dateWindow, effectiveMeetDate]);

  // Available hours from timeSlots
  const availableHours = useMemo(
    () => timeSlots.map((t) => parseInt(t.split(':')[0], 10)),
    [timeSlots],
  );

  // Compose meetTime string for the message
  const meetTime = `${String(meetHour).padStart(2, '0')}:${String(meetMinute).padStart(2, '0')}`;

  // Clamp hour to available range when it changes
  useEffect(() => {
    if (availableHours.length === 0) return;
    if (!availableHours.includes(meetHour)) {
      setMeetHour(availableHours[Math.floor(availableHours.length / 2)]);
    }
  }, [availableHours]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset scroll flag when sheet opens
  useEffect(() => {
    if (open) hasScrolledOnce.current = false;
  }, [open]);

  // Auto-scroll to pre-selected date once on open, then never again
  useEffect(() => {
    if (!effectiveMeetDate || hasScrolledOnce.current) return;
    hasScrolledOnce.current = true;
    const timer = setTimeout(() => {
      const el = dateScrollRef.current;
      if (!el) return;
      const activeBtn = el.querySelector('[data-active="true"]') as HTMLElement | null;
      if (activeBtn) {
        const left = activeBtn.offsetLeft - el.clientWidth / 2 + activeBtn.offsetWidth / 2;
        el.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [effectiveMeetDate]);

  if (!open) return null;

  const city = getCityName(group.city);

  const toggleFriend = (id: string) => {
    haptic();
    setSelectedFriends((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    haptic();
    setSelectedFriends(new Set(friends.map((f) => f.user.id)));
  };

  const dateOptions = intersectionDates;

  const effectiveMessage = quickPick || message.trim();
  const canSend = selectedFriends.size > 0 && effectiveMessage.length > 0;

  // Format the meetup details for the message
  const meetupLine = effectiveMeetDate
    ? `${city}, ${formatDateShort(effectiveMeetDate)} at ${formatTime12(meetTime)}`
    : `${city}`;

  const handleSend = async () => {
    if (!canSend || sending) return;
    hapticMedium();
    setSending(true);

    const selectedIds = [...selectedFriends];
    const rangeStart = dateWindow?.start || group.rangeStart;
    const rangeEnd = dateWindow?.end || group.rangeEnd;

    // Build proposal metadata for group messages
    const proposalMetadata: MeetupProposalMetadata | undefined =
      selectedIds.length >= 2 && effectiveMeetDate
        ? {
            type: 'meetup_proposal',
            city: group.city,
            date: effectiveMeetDate,
            time: meetTime,
            votes: { [currentUser.id]: 'yes' }, // creator auto-votes yes
          }
        : undefined;

    try {
      setError(null);
      if (selectedIds.length >= 2) {
        const groupName = generateGroupChatName(group.city, rangeStart, rangeEnd);
        const chat = await createGroupChat(groupName, selectedIds, group.city, rangeStart, rangeEnd);
        if (chat) {
          // Get all group member IDs (including creator)
          const allMemberIds = [currentUser.id, ...selectedIds.filter((id) => id !== currentUser.id)];

          // Create meetup entity
          const meetup = effectiveMeetDate
            ? await createMeetup({
                groupChatId: chat.id,
                city: group.city,
                date: effectiveMeetDate,
                time: meetTime,
                activity: quickPick?.replace('?', '') || undefined,
                memberIds: allMemberIds,
              })
            : null;

          // Set meetupId on proposal metadata if meetup was created
          const finalMetadata = proposalMetadata
            ? { ...proposalMetadata, ...(meetup ? { meetupId: meetup.id } : {}) }
            : undefined;

          await sendGroupMessage(chat.id, `${effectiveMessage}\n\n${meetupLine}`, finalMetadata);

          setSent(true);
          // App Store review — first meetup is the ultimate aha moment
          maybeRequestReview().catch(() => {});
          setTimeout(() => {
            onClose();
            navigate(`/messages?groupChat=${chat.id}&origin=calendar`);
          }, 1200);
        } else {
          setError('Failed to create group. Please try again.');
        }
      } else {
        const friendId = selectedIds[0];
        await sendMessage(friendId, `${effectiveMessage}\n\n${meetupLine}`);
        setSent(true);
        setTimeout(onClose, 1200);
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleQuickPick = (msg: string) => {
    haptic();
    if (quickPick === msg) {
      setQuickPick(null);
    } else {
      setQuickPick(msg);
      setMessage('');
    }
  };

  const weekday = (d: string) => new Date(d + 'T00:00:00').toLocaleDateString('en', { weekday: 'short' });

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-slate-800 rounded-t-2xl border-t border-slate-700 animate-slide-up"
        style={{ paddingBottom: 'calc(var(--sab, 0px) + 16px)' }}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 rounded-full bg-slate-600" />
        </div>

        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-slate-700 text-slate-400"
        >
          <X size={18} />
        </button>

        <div className="px-5 pb-4">
          {/* Header */}
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-10 h-10 rounded-full bg-coral/20 flex items-center justify-center">
              <Users size={20} className="text-coral" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-100">Plan Meetup</h3>
              <p className="text-xs text-slate-500">
                {meetupLine}
              </p>
            </div>
          </div>

          {/* Sent confirmation */}
          {sent ? (
            <div className="flex flex-col items-center gap-2 py-6">
              <div className="w-12 h-12 rounded-full bg-sage/20 flex items-center justify-center">
                <Check size={24} className="text-sage" />
              </div>
              <span className="text-sm font-medium text-sage">
                {selectedFriends.size >= 2 ? 'Group chat created!' : 'Sent!'}
              </span>
            </div>
          ) : (
            <>
              {/* Friend selection */}
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Who's joining?
                  </span>
                  {friends.length > 1 && selectedFriends.size < friends.length && (
                    <button onClick={selectAll} className="text-xs text-accent active:opacity-70">
                      Select all
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {friends.map((fs) => {
                    const selected = selectedFriends.has(fs.user.id);
                    const airline = getAirline(fs.airlineCode);
                    return (
                      <button
                        key={fs.user.id}
                        onClick={() => toggleFriend(fs.user.id)}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border transition-all active:scale-95 ${
                          selected
                            ? 'bg-coral/15 border-coral/40 text-slate-100'
                            : 'bg-slate-700/50 border-slate-600/50 text-slate-400'
                        }`}
                      >
                        <span
                          className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 overflow-hidden"
                          style={{ backgroundColor: airline.color, color: airline.textOnColor }}
                        >
                          <AvatarImg src={fs.user.avatarUrl} name={fs.user.name} />
                        </span>
                        <span className="text-xs font-medium">{fs.user.name.split(' ')[0]}</span>
                        {selected && <Check size={12} className="text-coral" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Overlap timeline — reuses the existing CityOverlapCard Gantt */}
              {selectedFriends.size > 0 && (
                <div className="mb-3">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">
                    Overlap Timeline
                  </span>
                  <CityOverlapCard
                    group={filteredGroup}
                    currentUserName={currentUser.name}
                    currentUserHandle={currentUser.handle}
                    currentUserAirline={currentUser.airlineCode}
                    currentUserAvatarUrl={currentUser.avatarUrl}
                    focusDate={effectiveMeetDate || undefined}
                  />
                </div>
              )}

              {/* Date + Time picker — single row */}
              {dateWindow && (
                <div className="mb-4">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">
                    When?
                  </span>
                  <div className="flex items-center gap-2">
                    {/* Date pills — scrollable */}
                    <div ref={dateScrollRef} className="flex gap-1.5 overflow-x-auto scrollbar-hide min-w-0 flex-1">
                      {dateOptions.map((d) => {
                        const isActive = effectiveMeetDate === d;
                        return (
                          <button
                            key={d}
                            data-active={isActive || undefined}
                            onClick={() => { haptic(); setMeetDate(d); }}
                            className={`shrink-0 px-2.5 py-1.5 rounded-lg text-xs transition-all active:scale-95 ${
                              isActive
                                ? 'bg-accent text-white font-bold'
                                : 'bg-slate-700 text-slate-300 font-medium'
                            }`}
                          >
                            <span className="block text-[10px] opacity-70">{weekday(d)}</span>
                            {formatDateShort(d)}
                          </button>
                        );
                      })}
                    </div>

                    {/* Time — native barrel picker on iOS, HTML time input on web */}
                    {Capacitor.isNativePlatform() ? (
                      <button
                        onClick={async () => {
                          haptic();
                          const minH = availableHours.length > 0 ? availableHours[0] : undefined;
                          const maxH = availableHours.length > 0 ? availableHours[availableHours.length - 1] : undefined;
                          const result = await showTimePicker(meetHour, meetMinute, 5, minH, maxH);
                          if (result) {
                            setMeetHour(result.hour);
                            setMeetMinute(result.minute);
                          }
                        }}
                        className="shrink-0 bg-slate-700 text-slate-100 text-xs font-semibold rounded-lg px-3 py-2.5 border border-slate-600 active:scale-95 transition-all"
                      >
                        {formatTime12(meetTime)}
                      </button>
                    ) : (
                      <input
                        type="time"
                        value={meetTime}
                        step={300}
                        min={availableHours.length > 0 ? `${String(availableHours[0]).padStart(2, '0')}:00` : undefined}
                        max={availableHours.length > 0 ? `${String(availableHours[availableHours.length - 1]).padStart(2, '0')}:59` : undefined}
                        onChange={(e) => {
                          haptic();
                          const [h, m] = e.target.value.split(':').map(Number);
                          if (!isNaN(h)) setMeetHour(h);
                          if (!isNaN(m)) setMeetMinute(m);
                        }}
                        className="shrink-0 bg-slate-700 text-slate-100 text-xs font-semibold rounded-lg px-3 py-2.5 border border-slate-600 focus:outline-none focus:border-accent/60 cursor-pointer [color-scheme:dark]"
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Quick messages */}
              <div className="mb-3">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">
                  Quick message
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {QUICK_MESSAGES.map((msg) => (
                    <button
                      key={msg}
                      onClick={() => handleQuickPick(msg)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all active:scale-95 ${
                        quickPick === msg
                          ? 'bg-accent text-white'
                          : 'bg-slate-700/60 text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      {msg}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom message */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Or type a custom message..."
                  value={message}
                  onChange={(e) => {
                    setMessage(e.target.value);
                    if (e.target.value) setQuickPick(null);
                  }}
                  className="w-full px-3 py-2.5 rounded-xl bg-slate-700/50 border border-slate-600/50 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-accent/50"
                />
              </div>

              {/* Error message */}
              {error && (
                <p className="text-xs text-red-400 text-center mb-2">{error}</p>
              )}

              {/* Send button */}
              <button
                onClick={handleSend}
                disabled={!canSend || sending}
                className={`w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-all ${
                  canSend
                    ? 'bg-coral text-white'
                    : 'bg-slate-700 text-slate-500'
                }`}
              >
                {sending ? (
                  <span className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />
                ) : (
                  <>
                    <Send size={14} />
                    {selectedFriends.size >= 2
                      ? `Create group (${selectedFriends.size + 1})`
                      : `Send to ${selectedFriends.size} friend`}
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

